import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { MonitoringService } from 'src/app/services/monitoring.service';
import {
  PrroLogEntry,
  PrroLogQueue,
} from 'src/app/services/prro-log-queue.service';
import { APP_CONFIG, SettingsService } from 'src/app/services/settings.service';
import { StorageService } from 'src/app/services/storage.service';
import { TenantAuthService } from 'src/app/services/tenant-auth.service';
import { FakePrroStore } from './fake-prro-store.service';
import {
  PrroLogLimits,
  PrroLogRequest,
  PrroRegisterState,
  isPrroLogLevelEnabled,
  toPrroLogRequest,
  toPrroRegisterState,
  trimPrroLogEntries,
} from './prro-log';
import type { ITaxConfig } from './tax-register-config.service';
import { TaxRegisterContextService } from './tax-register-context.service';

// The config of a register, for one without a context: TaxService's lookup.
export type PrroRegisterConfigLookup = (
  fiscalNumber: string,
) => Promise<Pick<ITaxConfig, 'tin' | 'ipn'> | null | undefined>;

// The entries not sent yet, of every tenant: each goes in a request of its own tenant.
const ENTRIES_KEY = 'PRRO_LOG_ENTRIES';
const LIMITS: PrroLogLimits = {
  maxEntries: 1000,
  maxCharacters: 2_000_000,
  maxAgeMs: 7 * 24 * 60 * 60 * 1000,
};
const BATCH_SIZE = 100;
// How long an entry waits to be sent, by level; this many waiting go at once.
const SEND_DELAYS: Record<PrroLogEntry['level'], number> = {
  Error: 2_000,
  Warning: 10_000,
  Information: 30_000,
};
const SEND_AT_ONCE = 50;
const RETRY_DELAY = 30_000;
const MAX_RETRY_DELAY = 5 * 60_000;
const SAVE_DELAY = 2_000;
// A changed state goes after STATE_DELAY; every state goes each HEARTBEAT, so the server sees
// the device is alive.
const STATE_DELAY = 3_000;
const STATE_RETRY_DELAY = 60_000;
const FIRST_HEARTBEAT = 30_000;
const HEARTBEAT = 15 * 60_000;
// The token interceptor's parameters: no offline switch, toast or token refresh for these
// background requests, a 20 s timeout, and the response as received.
const REQUEST_OPTIONS = {
  params: { skip: 'true', timeout: '20', responseFormater: 'none' },
};

// Sends the tax register (PRRO) logs and the registers' states to the server (Nt.Pos.Api:
// POST prro/logs, PUT prro/registers/{fiscalNumber}/state), where the admin console reads them.
// The logs wait on the device until the server takes them. A failed request is tried again
// later and is never logged through the tax register logger, which would feed it. The
// tax_server_logs setting turns it all off; tax_server_log_level sets the lowest level sent.
@Injectable({ providedIn: 'root' })
export class PrroLogService {
  private _entries: PrroLogEntry[] = [];
  private _started = false;
  private _loaded: Promise<void> = Promise.resolve();
  private _sending = false;
  private _failures = 0;
  private _retryAt = 0;
  private _sendTimer: ReturnType<typeof setTimeout> | null = null;
  private _sendDue = 0;
  private _saveTimer: ReturnType<typeof setTimeout> | null = null;
  private _stateTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // JSON of the last state sent, by tenant and register.
  private _sentStates = new Map<string, string>();
  private _organizations = new Map<string, Promise<{ tin?: string; ipn?: string }>>();
  private _stateReports = new Map<string, Promise<void>>();
  private _stateReportsPending = new Set<string>();
  private _getRegisterConfig: PrroRegisterConfigLookup = async () => null;

  constructor(
    private _http: HttpClient,
    private _queue: PrroLogQueue,
    private _settings: SettingsService,
    private _tenantAuth: TenantAuthService,
    private _storage: StorageService,
    private _context: TaxRegisterContextService,
    private _fakePrro: FakePrroStore,
    private _monitoring: MonitoringService,
    private _zone: NgZone,
  ) {}

  // Starts with the entries the device kept: then sends what is logged, a register's state
  // when it changes, and every state each 15 minutes. Calls after the first do nothing.
  start(getRegisterConfig?: PrroRegisterConfigLookup): void {
    if (this._started) {
      return;
    }
    this._started = true;
    if (getRegisterConfig) {
      this._getRegisterConfig = getRegisterConfig;
    }
    this._zone.runOutsideAngular(() => {
      this._loaded = this._load();
      this._queue.added.subscribe(() =>
        this._zone.runOutsideAngular(() => this.collect()),
      );
      this._context.stateChanged.subscribe((fiscalNumber) =>
        this._zone.runOutsideAngular(() => this._onStateChanged(fiscalNumber)),
      );
      void this._loaded.then(() => {
        this.collect();
        this._scheduleSend(0);
      });
      setTimeout(() => void this.reportStates(), FIRST_HEARTBEAT);
      setInterval(() => void this.reportStates(), HEARTBEAT);
    });
  }

  // Takes the logged entries: those the settings let through wait to be sent.
  collect(): void {
    const entries = this._queue.take();
    if (!this._settings.taxServerLogs) {
      return;
    }
    const minimum = this._settings.taxServerLogLevel;
    const accepted = entries.filter((entry) =>
      isPrroLogLevelEnabled(entry.level, minimum),
    );
    if (!accepted.length) {
      return;
    }

    this._entries = trimPrroLogEntries(
      [...this._entries, ...accepted],
      new Date(),
      LIMITS,
    );
    this._scheduleSave();
    this._scheduleSend(
      this._entries.length >= SEND_AT_ONCE
        ? 0
        : Math.min(...accepted.map((entry) => SEND_DELAYS[entry.level])),
    );
  }

  // Sends the current tenant's entries, a batch at a time; after a failure, tries again later.
  async flush(): Promise<void> {
    if (this._sending) {
      return;
    }
    this._sending = true;
    try {
      await this._loaded;
      while (await this._sendBatch()) {}
      this._failures = 0;
      this._retryAt = 0;
    } catch (error) {
      this._failures++;
      const delay = Math.min(
        RETRY_DELAY * 2 ** (this._failures - 1),
        MAX_RETRY_DELAY,
      );
      this._retryAt = Date.now() + delay;
      this._scheduleSend(delay);
      console.warn('PRRO logs not sent to the server.', (error as { status?: number })?.status);
    } finally {
      this._sending = false;
    }
  }

  // Sends the register's state: when it differs from the last one sent, or always.
  reportState(fiscalNumber: string, always = false): Promise<void> {
    const tenantId = this._tenantAuth.tenantId;
    if (!tenantId || !this._settings.taxServerLogs) {
      return Promise.resolve();
    }

    const key = `${tenantId}|${fiscalNumber}`;
    const running = this._stateReports.get(key);
    if (running) {
      // A change during a slow request still needs a report after it finishes.
      this._stateReportsPending.add(key);
      return running;
    }
    const report = this._reportState(fiscalNumber, tenantId, always).finally(() => {
      this._stateReports.delete(key);
      if (
        this._stateReportsPending.delete(key) &&
        !this._stateTimers.has(fiscalNumber)
      ) {
        this._scheduleState(fiscalNumber, STATE_DELAY);
      }
    });
    this._stateReports.set(key, report);
    return report;
  }

  private async _reportState(
    fiscalNumber: string,
    tenantId: string,
    always: boolean,
  ): Promise<void> {
    try {
      const state = await this._getState(fiscalNumber);
      if (!state) {
        return;
      }
      const key = `${tenantId}|${fiscalNumber}`;
      const json = JSON.stringify(state);
      if (!always && this._sentStates.get(key) === json) {
        return;
      }
      await this._http
        .put(
          `${await this._getApiEndpoint()}/prro/registers/${encodeURIComponent(fiscalNumber)}/state`,
          state,
          REQUEST_OPTIONS,
        )
        .toPromise();
      this._sentStates.set(key, json);
    } catch (error) {
      // Otherwise the next heartbeat sends it.
      if (isTransient(error)) {
        this._scheduleState(fiscalNumber, STATE_RETRY_DELAY);
      }
    }
  }

  // The states of all the registers on the device, and the waiting entries.
  async reportStates(): Promise<void> {
    if (!this._tenantAuth.tenantId || !this._settings.taxServerLogs) {
      return;
    }

    void this.flush();
    try {
      for (const fiscalNumber of await this._context.getTaxRegisterIds()) {
        await this.reportState(fiscalNumber, true);
      }
    } catch {
      // The next heartbeat tries again.
    }
  }

  // One batch of the current tenant's entries; false when there is none to send.
  private async _sendBatch(): Promise<boolean> {
    const tenantId = this._tenantAuth.tenantId;
    if (!tenantId || !this._settings.taxServerLogs) {
      return false;
    }
    const batch = this._entries
      .filter((entry) => !entry.tenantId || entry.tenantId === tenantId)
      .slice(0, BATCH_SIZE);
    if (!batch.length) {
      return false;
    }

    const requests = await Promise.all(
      batch.map((entry) => this._toRequest(entry, tenantId)),
    );
    try {
      await this._http
        .post(`${await this._getApiEndpoint()}/prro/logs`, requests, REQUEST_OPTIONS)
        .toPromise();
    } catch (error) {
      if (!isRefused(error)) {
        throw error;
      }
      // Refused once, refused always: the batch is dropped, not sent forever.
      console.warn('PRRO logs refused by the server.', (error as { status?: number }).status);
    }

    const sent = new Set(batch.map((entry) => entry.id));
    this._entries = this._entries.filter((entry) => !sent.has(entry.id));
    this._scheduleSave();
    return true;
  }

  // The entry as the server takes it, with the TIN and IPN of its register.
  private async _toRequest(
    entry: PrroLogEntry,
    tenantId: string,
  ): Promise<PrroLogRequest> {
    const request = toPrroLogRequest(entry);
    if (!request.taxRegisterFiscalNumber || request.tin || request.ipn) {
      return request;
    }
    return {
      ...request,
      ...(await this._getOrganization(tenantId, request.taxRegisterFiscalNumber)),
    };
  }

  // The register's TIN and IPN: from its context, or from its config when it has none.
  private async _getOrganization(
    tenantId: string,
    fiscalNumber: string,
  ): Promise<{ tin?: string; ipn?: string }> {
    const key = `${tenantId}|${fiscalNumber}`;
    const known = this._organizations.get(key);
    if (known) {
      return known;
    }

    // A batch resolves up to 100 entries concurrently. Share the pending read,
    // not just its result, so one register needs only one config lookup.
    const lookup = (async () => {
      const config =
        (await this._context.getTaxConfig(fiscalNumber).catch(() => null)) ||
        (await this._getRegisterConfig(fiscalNumber).catch(() => null));
      return {
        tin: config?.tin?.trim() || undefined,
        ipn: config?.ipn?.trim() || undefined,
      };
    })();
    this._organizations.set(key, lookup);
    const organization = await lookup;
    if (
      !organization.tin &&
      !organization.ipn &&
      this._organizations.get(key) === lookup
    ) {
      this._organizations.delete(key);
    }
    return organization;
  }

  private async _getState(
    fiscalNumber: string,
  ): Promise<PrroRegisterState | null> {
    const context = await this._context.getTaxContext(fiscalNumber);
    if (!context) {
      return null;
    }
    const [state, documents, disabled] = await Promise.all([
      this._context.getState(fiscalNumber),
      this._context.getDocuments(fiscalNumber),
      this._context.isDisabled(fiscalNumber),
    ]);
    return toPrroRegisterState(context, state, documents, {
      disabled,
      fakeSession: await this._fakePrro.isSession(state.OfflineSessionId),
      recoveryMode: this._settings.taxRecoveryMode,
      appVersion: this._monitoring.versionNumber,
    });
  }

  private _onStateChanged(fiscalNumber: string): void {
    if (!fiscalNumber) {
      return;
    }
    // A new context may come with another config.
    this._organizations.delete(`${this._tenantAuth.tenantId}|${fiscalNumber}`);
    if (this._settings.taxServerLogs) {
      this._scheduleState(fiscalNumber, STATE_DELAY);
    }
  }

  private _scheduleState(fiscalNumber: string, delay: number): void {
    clearTimeout(this._stateTimers.get(fiscalNumber));
    this._stateTimers.set(
      fiscalNumber,
      setTimeout(() => {
        this._stateTimers.delete(fiscalNumber);
        void this.reportState(fiscalNumber);
      }, delay),
    );
  }

  // The send at `delay` from now, or when the wait after a failure ends. An earlier send
  // already planned stays, unless it falls in that wait.
  private _scheduleSend(delay: number): void {
    const due = Math.max(Date.now() + delay, this._retryAt);
    if (
      this._sendTimer &&
      this._sendDue <= due &&
      this._sendDue >= this._retryAt
    ) {
      return;
    }
    clearTimeout(this._sendTimer);
    this._sendDue = due;
    this._sendTimer = setTimeout(() => {
      this._sendTimer = null;
      void this.flush();
    }, due - Date.now());
  }

  private _scheduleSave(): void {
    if (this._saveTimer) {
      return;
    }
    this._saveTimer = setTimeout(() => {
      this._saveTimer = null;
      void this._save();
    }, SAVE_DELAY);
  }

  // The kept entries before those logged since the start.
  private async _load(): Promise<void> {
    try {
      const stored = (await this._storage.get(ENTRIES_KEY)) as
        | PrroLogEntry[]
        | null;
      const logged = new Set(this._entries.map((entry) => entry.id));
      this._entries = [
        ...(stored || []).filter((entry) => entry?.id && !logged.has(entry.id)),
        ...this._entries,
      ];
    } catch {
      // Nothing kept can be read: the entries logged from now on are kept.
    }
  }

  private async _save(): Promise<void> {
    await this._loaded;
    try {
      await this._storage.set(ENTRIES_KEY, this._entries);
    } catch {
      // Kept in memory; the next save tries again.
    }
  }

  private async _getApiEndpoint(): Promise<string> {
    const configuredEndpoint = await this._settings._getValue<string>(
      APP_CONFIG.POS_API_ENDPOINT,
    );
    return configuredEndpoint.replace(/\/+$/, '');
  }
}

// A request the server refuses as such (bad request, too large): sending it again fails again.
function isRefused(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  return status === 400 || status === 413 || status === 422;
}

// A failure that may pass soon: no network, a timeout, a server error, or none from the server.
function isTransient(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  return (
    !status || status === 408 || status === 429 || status >= 500
  );
}
